import React, { useCallback, useMemo, useRef } from 'react';
import { Divider, FormControlLabel, Stack, Switch } from '@mui/material';
import MoneyTextField from 'common/components/MoneyTextField';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Box } from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ActiveProductActions from 'actions/ActiveProductActions';

import useDebouncedForm from 'common/hooks/useDebouncedForm';

import HasFeature from 'components/features/HasFeature';
import { useTranslation } from 'services/localization/useTranslation';
import VariantSalesPriceTable from './components/VariantSalesPriceTable';
import { ProductVariant } from 'common/modules/inventory';
import { isAnyVariantForSale } from 'common/modules/products/utils';
import Banner from 'components/Banner';
import { hasMultipleVariants } from 'common/modules/products/variants';

const SalesPricing = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const variantTableRef = useRef<HTMLDivElement>(null);

	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const currency = useSelector(ShopSelectors.getShopCurrency);
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);

	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(
			() => ({
				sales: product?.sales ?? {
					basePrice: 0,
					enabled: false,
				},
				variants: product?.variants ?? {
					properties: [],
					options: [],
				},
			}),
			[product],
		),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(ActiveProductActions.editLocalProduct(changes));
			},
			[dispatch],
		),
	});

	const areSalesEnabled = !!state?.sales.enabled;

	const handleBasePriceChange = (basePrice: number) => {
		editState({ sales: { ...state.sales, basePrice } });
	};

	const handleEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		editState({
			sales: {
				...state.sales,
				enabled: event.target.checked,
			},
			variants: {
				...state.variants,
				options: state.variants.options.map((variant) => {
					return {
						...variant,
						sales: variant.sales
							? {
									...variant.sales,
									enabled: event.target.checked,
							  }
							: {
									enabled: event.target.checked,
									priceOverride: null,
							  },
					};
				}),
			},
		});
	};

	const handleVariantsChange = (variants: ProductVariant[]) => {
		editState({
			variants: {
				...state.variants,
				options: variants,
			},
		});
	};

	if (!product) return null;

	return (
		<>
			<Box p={2}>
				<FormControlLabel
					control={
						<Switch
							checked={state.sales.enabled}
							onChange={handleEnabledChange}
							disabled={isEditingDisabled}
						/>
					}
					label={
						<Typography
							variant="h6"
							sx={{
								opacity: isEditingDisabled ? 0.3 : 1,
							}}
						>
							{t('product.sales', 'Sales')}
						</Typography>
					}
				/>
			</Box>
			{areSalesEnabled && !isAnyVariantForSale(state.variants.options) && (
				<Banner
					variant="warning"
					title={t('product.noVariantsForSale', 'No variants for sale')}
					description={t(
						'product.noVariantsForSaleDescription',
						'None of your variants have been marked as for sale',
					)}
				/>
			)}
			<ContainerBox>
				<Typography
					variant="h6"
					sx={{
						opacity: !!isEditingDisabled || !areSalesEnabled ? 0.3 : 1,
					}}
				>
					{t('product.salesPrice', 'Sales price')}
				</Typography>
				<Stack direction="row" sx={{ mt: 4 }}>
					<MoneyTextField
						label={t('product.basePrice', 'Base price')}
						value={state.sales.basePrice}
						onValueChange={handleBasePriceChange}
						useMinorUnits
						variant="outlined"
						currency={currency}
						InputLabelProps={{ shrink: true }}
						disabled={!areSalesEnabled || isEditingDisabled}
					/>
				</Stack>
				{hasMultipleVariants(product) && (
					<HasFeature requiredFeature="VARIANTS">
						<Box>
							<Divider sx={{ mt: 3, mb: 3 }} />
							<div ref={variantTableRef} />
							<VariantSalesPriceTable
								productId={product.id}
								basePrice={state.sales.basePrice ?? 0}
								variants={state.variants.options}
								onVariantsChange={handleVariantsChange}
								currency={currency}
								disabled={isEditingDisabled || !areSalesEnabled}
							/>
						</Box>
					</HasFeature>
				)}
			</ContainerBox>
		</>
	);
};

export default SalesPricing;
